/**
 * WP-1 — Storefront step-transition emitters.
 *
 * Closes the "dead wire" leak: before this module the storefront only ever sent
 * `page_view`, so authored `cart_updated` / `order_created` / `order_updated` /
 * `timeout` transitions could never fire. Each emitter does two things:
 *
 *  1. drives a server-side step evaluation with the *transition* event name
 *     (the enum funnels-go matches edges on: see STEP_TRANSITION_EVENTS), and
 *  2. emits the *plain behavioral mirror* on the in-browser bus so listener
 *     reactions (WP-2) and the transport leg (D-G) observe the same action.
 *
 * Emitters are idempotent per logical action (see the dedupe strategies below)
 * so a single cart mutation or order creation can never double-fire.
 */

import {
  FUNNEL_UI_EVENT_CATALOG,
  STEP_TRANSITION_EVENTS,
  type StepTransitionEvent,
  type UIEventOrigin,
} from '@trafilea/tss-lib-funnel-schema-ts';
import type { FunnelEvaluateStepRequest, FunnelEvaluateStepResponse, PageContext } from '../types';
import type { FunnelEventBus } from './funnel-event-bus';
import { createEventEnvelope, type UIEventPayload } from './envelope';
import { isFunnelUiEventsEnabled } from './feature-flag';
import { debugLogFunnelEvent } from './debug-logger';

/** Plain behavioral mirror emitted on the bus for each transition event (D-G). */
export const TRANSITION_TO_BEHAVIORAL_MIRROR = {
  cart_updated: 'add_to_cart',
  order_created: 'order_completed',
  order_updated: 'order_updated',
  timeout: 'timer_expired',
} as const satisfies Partial<Record<StepTransitionEvent, string>>;

export type EmittableTransitionEvent = keyof typeof TRANSITION_TO_BEHAVIORAL_MIRROR;
export type FunnelBehavioralEvent = (typeof FUNNEL_UI_EVENT_CATALOG)[number];

/** Compile-time guard: every emittable event is a real transition event. */
const _assertEmittableAreTransitions: readonly StepTransitionEvent[] = Object.keys(
  TRANSITION_TO_BEHAVIORAL_MIRROR
) as StepTransitionEvent[];
void _assertEmittableAreTransitions;

export interface DedupeStore {
  has(key: string): boolean;
  add(key: string): void;
  delete(key: string): void;
}

/**
 * sessionStorage-backed dedupe for durable, cross-render actions (orders).
 * Falls back to an in-memory set when storage is unavailable (SSR/private mode).
 */
/**
 * Behavioral event names with a REAL wired producer today (T2 honesty source):
 * renderer/behavioral-tracker emissions + the transition mirrors above. The
 * integrity-matrix gate cross-checks its EMITTED cells against this list —
 * add a name here ONLY when its emitter ships.
 */
export const PRODUCED_BEHAVIORAL_EVENTS: readonly string[] = [
  // Renderer + behavioral-tracker emissions (authored bindings / auto):
  'cta_clicked',
  'lead_captured',
  'coupon_revealed',
  'spin_completed',
  'experience_dismissed',
  'block_viewed',
  'form_submitted',
  'scroll_depth_reached',
  'exit_intent',
  'offer_accepted',
  'offer_declined',
  'checkout_started',
  'variant_selected',
  'coupon_applied',
  'remove_from_cart',
  // Transition behavioral mirrors (emitBehavioralMirror):
  ...Object.values(TRANSITION_TO_BEHAVIORAL_MIRROR),
];

export function createSessionDedupeStore(namespace: string): DedupeStore {
  const memory = new Set<string>();
  const storageKey = (key: string) => `funnel:emit:${namespace}:${key}`;
  return {
    has(key: string): boolean {
      if (memory.has(key)) return true;
      try {
        return sessionStorage.getItem(storageKey(key)) !== null;
      } catch {
        return false;
      }
    },
    add(key: string): void {
      memory.add(key);
      try {
        sessionStorage.setItem(storageKey(key), '1');
      } catch {
        /* ignore — memory set still guards this render tree */
      }
    },
    delete(key: string): void {
      memory.delete(key);
      try {
        sessionStorage.removeItem(storageKey(key));
      } catch {
        /* ignore — memory set has already been cleared */
      }
    },
  };
}

/** In-memory dedupe for per-session, non-durable actions (cart debounce, timeout). */
export function createMemoryDedupeStore(): DedupeStore {
  const memory = new Set<string>();
  return {
    has: (key: string) => memory.has(key),
    add: (key: string) => {
      memory.add(key);
    },
    delete: (key: string) => {
      memory.delete(key);
    },
  };
}

export interface StepEventEmitterDeps {
  evaluatePageStep: (
    request: Omit<FunnelEvaluateStepRequest, 'sessionId' | 'funnelId'> & { funnelId?: string }
  ) => Promise<FunnelEvaluateStepResponse>;
  eventBus: FunnelEventBus;
  sessionId: string;
  scope: { tenantId: string; storeId?: string };
  /** Active funnel id; emitters no-op when null (no live session). */
  getFunnelId: () => string | null;
  /** Current step id, so the server can resolve the outgoing edge. */
  getCurrentStepId?: () => string | undefined;
  /** Gate (defaults to the funnel-ui-events flag). */
  isEnabled?: () => boolean;
  /** Observer for best-effort transition failures that must not fail commerce. */
  onTransitionError?: (event: StepTransitionEvent, error: unknown) => void;
  /** Injectable dedupe stores (tests). */
  orderCreatedStore?: DedupeStore;
  orderUpdatedStore?: DedupeStore;
  cartStore?: DedupeStore;
  timeoutStore?: DedupeStore;
}

export interface CartUpdatedInput {
  pageContext: Partial<PageContext>;
  /** Commerce meaning of this cart mutation; defaults to add_to_cart for compatibility. */
  behavioralEvent?: 'add_to_cart' | 'remove_from_cart';
  /**
   * False when the centralized dispatcher owns the correlated result. The
   * cart_updated transition still runs; only the duplicate behavioral mirror
   * is suppressed.
   */
  emitBehavioralResult?: boolean;
  /** Behavioral-mirror params (sku, quantity, variantId, …) for reactions. */
  params?: Record<string, unknown>;
  /** Link this UI observation to the canonical commerce fact without sharing its dedup key. */
  businessEventId?: string;
}

export interface BehavioralEventInput {
  event: FunnelBehavioralEvent;
  params?: Record<string, unknown>;
  pageContext?: Partial<PageContext>;
  /** Defaults to observation; confirmed component/domain changes must opt into result. */
  phase?: 'observation' | 'result';
  /** Identifies the layer that established the phase boundary. */
  origin?: UIEventOrigin;
  /** Link this UI observation to the successful business action, when available. */
  businessEventId?: string;
  /** Link a destination-owned result to its independently identified source intent. */
  correlationId?: string;
}

export interface OrderCreatedInput {
  orderId: string;
  currentStepId?: string;
  pageContext?: Partial<PageContext>;
  params?: Record<string, unknown>;
  businessEventId?: string;
}

export interface OrderUpdatedInput {
  orderId: string;
  currentStepId?: string;
  /** Distinguishes independent mutations on the same order (add/remove/upsell). */
  mutationKey: string;
  pageContext?: Partial<PageContext>;
  params?: Record<string, unknown>;
  businessEventId?: string;
}

export interface TimeoutInput {
  stepId: string;
  pageContext?: Partial<PageContext>;
}

export interface StepEventEmitters {
  emitBehavioralEvent: (input: BehavioralEventInput) => void;
  emitCartUpdated: (input: CartUpdatedInput) => Promise<FunnelEvaluateStepResponse | null>;
  emitOrderCreated: (input: OrderCreatedInput) => Promise<FunnelEvaluateStepResponse | null>;
  emitOrderUpdated: (input: OrderUpdatedInput) => Promise<FunnelEvaluateStepResponse | null>;
  emitTimeout: (input: TimeoutInput) => Promise<FunnelEvaluateStepResponse | null>;
}

function cartSignature(input: CartUpdatedInput): string {
  const { pageContext: ctx, params = {} } = input;
  return [
    ctx.cartToken ?? '',
    ctx.cartItemCount ?? '',
    ctx.cartTotal ?? '',
    (ctx.cartProductIds ?? []).join(','),
    input.behavioralEvent ?? 'add_to_cart',
    params.productId ?? '',
    params.variantId ?? '',
    params.sku ?? '',
    params.quantity ?? '',
    params.delta ?? '',
  ].join('|');
}

export function createStepEventEmitters(deps: StepEventEmitterDeps): StepEventEmitters {
  const isEnabled = deps.isEnabled ?? isFunnelUiEventsEnabled;
  const orderCreatedStore = deps.orderCreatedStore ?? createSessionDedupeStore('order_created');
  const orderUpdatedStore = deps.orderUpdatedStore ?? createSessionDedupeStore('order_updated');
  const cartStore = deps.cartStore ?? createMemoryDedupeStore();
  const timeoutStore = deps.timeoutStore ?? createMemoryDedupeStore();

  function emitBehavioralMirror(
    event: EmittableTransitionEvent,
    params: Record<string, unknown>,
    context: UIEventPayload['context'],
    businessEventId?: string,
    behavioralEvent?: FunnelBehavioralEvent
  ): void {
    if (!isEnabled()) return;
    const name = behavioralEvent ?? TRANSITION_TO_BEHAVIORAL_MIRROR[event];
    const payload: UIEventPayload = {
      params: {
        ...params,
        ...(businessEventId ? { businessEventId } : {}),
      },
      context,
    };
    deps.eventBus.emit(
      createEventEnvelope(name, 'storefront-funnel', payload, {
        sessionId: deps.sessionId,
        phase: 'result',
        origin: 'domain_authority',
        scope: deps.scope,
      })
    );
  }

  async function evaluateTransition(
    event: StepTransitionEvent,
    pageContext: Partial<PageContext>,
    currentStepId?: string
  ): Promise<FunnelEvaluateStepResponse | null> {
    const funnelId = deps.getFunnelId();
    if (!funnelId) return null;
    // T9: the WIRE leg honors the master kill switch too — with the flag off the
    // storefront is byte-identical to the pre-event-system behavior (stored
    // add_to_cart/purchase/timer edges stay dormant until activation).
    if (!isEnabled()) return null;
    debugLogFunnelEvent('wire-transition', {
      event,
      currentStepId: currentStepId ?? deps.getCurrentStepId?.() ?? null,
      funnelId,
    });
    // Defensive: only ever send names funnels-go can match as transitions.
    if (!STEP_TRANSITION_EVENTS.includes(event)) return null;
    return deps.evaluatePageStep({
      funnelId,
      // Prefer an explicitly-scoped step (e.g. the step whose timeout fired)
      // and fall back to the active step id from the provider.
      currentStepId: currentStepId ?? deps.getCurrentStepId?.(),
      event,
      // The provider merges a full base PageContext (device, url, session) into
      // this partial before hitting the API, so a partial is safe at runtime.
      pageContext: pageContext as PageContext,
    });
  }

  return {
    emitBehavioralEvent(input) {
      if (!isEnabled()) return;
      if (!FUNNEL_UI_EVENT_CATALOG.includes(input.event)) return;
      const funnelId = deps.getFunnelId();
      const payload: UIEventPayload = {
        params: {
          ...(input.params ?? {}),
          ...(input.businessEventId ? { businessEventId: input.businessEventId } : {}),
        },
        context: {
          funnelId: funnelId ?? undefined,
          stepId: deps.getCurrentStepId?.(),
          pageType: input.pageContext?.pageType,
        },
      };
      deps.eventBus.emit(
        createEventEnvelope(input.event, 'storefront-funnel', payload, {
          sessionId: deps.sessionId,
          correlationId: input.correlationId,
          phase: input.phase ?? 'observation',
          origin: input.origin ?? 'handler',
          scope: deps.scope,
        })
      );
    },

    async emitCartUpdated(input) {
      if (!deps.getFunnelId()) return null;
      const signature = cartSignature(input);
      if (cartStore.has(signature)) return null;
      cartStore.add(signature);

      if (input.emitBehavioralResult !== false) {
        emitBehavioralMirror(
          'cart_updated',
          input.params ?? {},
          {
            funnelId: deps.getFunnelId() ?? undefined,
            stepId: deps.getCurrentStepId?.(),
            pageType: input.pageContext.pageType,
          },
          input.businessEventId,
          input.behavioralEvent
        );
      }
      try {
        return await evaluateTransition('cart_updated', input.pageContext);
      } catch (error) {
        cartStore.delete(signature);
        // The cart mutation is already authoritative by the time this emitter
        // runs. Funnel evaluation is retryable telemetry/transition work and
        // must never turn a committed cart update into a shopper-facing error.
        deps.onTransitionError?.('cart_updated', error);
        return null;
      }
    },

    async emitOrderCreated(input) {
      if (!input.orderId || !deps.getFunnelId()) return null;
      if (orderCreatedStore.has(input.orderId)) return null;
      orderCreatedStore.add(input.orderId);

      const currentStepId = input.currentStepId ?? deps.getCurrentStepId?.();

      const pageContext: Partial<PageContext> = {
        pageType: 'post_purchase',
        orderId: input.orderId,
        ...input.pageContext,
      };
      emitBehavioralMirror(
        'order_created',
        { orderId: input.orderId, ...input.params },
        {
          funnelId: deps.getFunnelId() ?? undefined,
          stepId: input.currentStepId ?? deps.getCurrentStepId?.(),
          pageType: 'post_purchase',
        },
        input.businessEventId
      );
      try {
        const result = await evaluateTransition('order_created', pageContext, currentStepId);
        const advanced = Boolean(
          result?.hasExperience &&
            result.step?.id &&
            (!currentStepId || result.step.id !== currentStepId)
        );
        // Checkout evaluates before redirect and UpsellPage retries on mount.
        // Reserve the order-level dedupe key only after the transition actually
        // advances; a successful no-match/same-step response is still retryable.
        if (!advanced) {
          orderCreatedStore.delete(input.orderId);
        }
        return result;
      } catch (error) {
        orderCreatedStore.delete(input.orderId);
        throw error;
      }
    },

    async emitOrderUpdated(input) {
      if (!input.orderId || !input.mutationKey || !deps.getFunnelId()) return null;
      const dedupeKey = `${input.orderId}:${input.mutationKey}`;
      if (orderUpdatedStore.has(dedupeKey)) return null;
      orderUpdatedStore.add(dedupeKey);

      const pageContext: Partial<PageContext> = {
        pageType: 'post_purchase',
        orderId: input.orderId,
        ...input.pageContext,
      };
      emitBehavioralMirror(
        'order_updated',
        { orderId: input.orderId, mutationKey: input.mutationKey, ...input.params },
        {
          funnelId: deps.getFunnelId() ?? undefined,
          stepId: input.currentStepId ?? deps.getCurrentStepId?.(),
          pageType: 'post_purchase',
        },
        input.businessEventId
      );
      try {
        return await evaluateTransition('order_updated', pageContext, input.currentStepId);
      } catch (error) {
        orderUpdatedStore.delete(dedupeKey);
        throw error;
      }
    },

    async emitTimeout(input) {
      if (!deps.getFunnelId()) return null;
      const dedupeKey = `${deps.getFunnelId()}:${input.stepId}`;
      if (timeoutStore.has(dedupeKey)) return null;
      timeoutStore.add(dedupeKey);

      emitBehavioralMirror(
        'timeout',
        {},
        {
          funnelId: deps.getFunnelId() ?? undefined,
          stepId: input.stepId,
          pageType: input.pageContext?.pageType,
        }
      );
      try {
        return await evaluateTransition(
          'timeout',
          {
            ...input.pageContext,
          },
          input.stepId
        );
      } catch (error) {
        timeoutStore.delete(dedupeKey);
        throw error;
      }
    },
  };
}
